import { useEffect, useRef, useState } from "react";
import { t, getLang } from "@/lib/i18n";
import { X, Send, Sparkles, IndianRupee, Truck, Recycle, Users, HelpCircle, MapPin } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const SESSION_KEY = "ks_prana_session";
const TOPICS = [
  { icon: Truck, label: "How do I book?", prompt: "How do I book a scrap pickup with Kindsaver?" },
  { icon: IndianRupee, label: "Scrap rates", prompt: "What are the current scrap rates in Jaipur?" },
  { icon: Recycle, label: "What do you collect?", prompt: "Which items do you collect and which ones don't you?" },
  { icon: HelpCircle, label: "Segregation tips", prompt: "Give me quick tips to segregate waste at home." },
  { icon: Users, label: "I'm a kabadiwala", prompt: "I am a kabadiwala. How can I join Kindsaver?" },
  { icon: MapPin, label: "Which cities?", prompt: "Which cities is Kindsaver available in?" },
];

function Linkify({ text }) {
  const parts = text.replace(/\*\*(.+?)\*\*/g, "$1").replace(/^#+\s*/gm, "").split(/(https?:\/\/[^\s)]+|app\.kindsaver\.com[^\s)]*)/g);
  return parts.map((p, i) =>
    /^(https?:\/\/|app\.kindsaver\.com)/.test(p)
      ? <a key={i} href={p.startsWith("http") ? p : `https://${t(p)}`} target="_blank" rel="noreferrer" className="underline font-semibold text-[var(--leaf-dark)] break-all">{t(p)}</a>
      : <span key={i}>{t(p)}</span>
  );
}

export default function FloatingChatbot() {
  const { content } = useSite();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Namaste! I'm Prana. Ask me about booking a pickup, scrap rates, what we collect or how to sort waste at home. Hindi or English — both are fine." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(localStorage.getItem(SESSION_KEY) || crypto.randomUUID());
  const listRef = useRef(null);

  useEffect(() => { localStorage.setItem(SESSION_KEY, sessionId.current); }, []);
  useEffect(() => { listRef.current && (listRef.current.scrollTop = listRef.current.scrollHeight); }, [messages, loading, open]);

  const send = async (override) => {
    const text = (override ?? input).trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chatbot/chat", { session_id: sessionId.current, message: text, lang: getLang() });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: `Sorry — ${formatApiError(e)}. You can also call us at ${content.phone}.` }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen((v) => !v)} data-testid="chatbot-toggle" aria-label={open ? t("Close Prana chat") : t("Open Prana chat")}
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-50">
        {!open && <span className="prana-ring" aria-hidden="true" />}
        <span className="prana-orb relative flex items-center gap-2 pl-3 pr-4 py-3 rounded-full text-white">
          {open ? <X size={18} /> : <Sparkles size={18} />}
          <span className="text-sm font-semibold">{t(open ? t("Close") : t("Ask Prana"))}</span>
        </span>
      </button>

      {open && (
        <div data-testid="chatbot-window" className="fixed bottom-20 sm:bottom-24 right-3 sm:right-6 z-50 w-[min(94vw,400px)] h-[min(620px,78vh)] rounded-3xl bg-white shadow-[0_30px_80px_-30px_rgba(20,40,26,.5)] border border-[rgba(20,40,26,.08)] flex flex-col overflow-hidden fade-up">
          <div className="px-4 py-3.5 bg-[var(--leaf)] text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden"><img src="/icon-192.png" alt="" className="w-9 h-9" /></div>
            <div className="flex-1">
              <div className="text-sm font-bold flex items-center gap-1.5">{t("Prana")} <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded">{t("AI")}</span></div>
              <div className="text-[11px] opacity-85">{t("Kindsaver helper · may make mistakes")}</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/15" aria-label="Close" data-testid="chatbot-close"><X size={16} /></button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto p-3.5 space-y-2.5 bg-[var(--paper)]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[82%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[var(--ink)] text-white rounded-br-md" : "bg-white border border-[rgba(20,40,26,.08)] rounded-bl-md"}`}>
                  {m.role === "assistant" ? <Linkify text={t(m.text)} /> : m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="inline-flex bg-white border border-[rgba(20,40,26,.08)] px-3.5 py-3 rounded-2xl rounded-bl-md" data-testid="chatbot-typing">
                <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
              </div>
            )}
            {messages.length === 1 && !loading && (
              <div className="grid grid-cols-2 gap-2 pt-2" data-testid="prana-topic-grid">
                {TOPICS.map(({ icon: Icon, label, prompt }) => (
                  <button key={t(label)} onClick={() => send(prompt)} data-testid={`prana-topic-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="flex items-center gap-2 p-2.5 rounded-xl bg-white border border-[rgba(20,40,26,.08)] text-left text-xs font-semibold hover:border-[var(--leaf)] hover:-translate-y-0.5 transition-transform">
                    <span className="w-7 h-7 rounded-lg bg-[var(--leaf-soft)] text-[var(--leaf-dark)] flex items-center justify-center shrink-0"><Icon size={14} /></span>
                    {t(label)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-[rgba(20,40,26,.08)] flex items-center gap-2">
            <input data-testid="chatbot-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={t("Type your question…")} className="flex-1 px-4 py-2.5 rounded-full bg-[var(--paper)] border border-[rgba(20,40,26,.1)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--leaf)]/30" />
            <button onClick={() => send()} disabled={loading || !input.trim()} data-testid="chatbot-send" aria-label="Send"
              className="w-10 h-10 rounded-full bg-[var(--leaf)] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[var(--leaf-dark)] transition-colors">
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
